import { omit, replace } from 'lodash';
import { CommonResponse, CommonResponseList } from 'src/model/common';
import {
  BillOfLadingDetail,
  BuyerFromSuppliers,
  CompanyAggregatedByBOL,
  PartnersFromCompany,
  PortOriginTransactionProportions,
  SupplierFromBuyers,
  TradeDataBOLs,
  TradeDataEntity,
  TradeDataEntityDetail,
  TradeDataListTypeAIExplanation,
  TradeDataListTypeResponse,
  TradeDataMarketAnalytics,
  TradeDataMarketAnalyticsAIExplanation,
  TradeDataMarketAnalyticsCompany,
  TradeDataMarketAnalyticsWorldMap,
  TradeDataPort,
  TradeDataProduct,
  TradeDataProductDetail,
} from 'src/model/tradeData';
import { getBlobRequest, getRequest } from 'src/services/api/http';
import { TypeBuyerSupplier, TypeDataPackage } from 'src/utils/constants/enum';
import { URL_ENDPOINT } from 'src/utils/constants/service';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { handleQueryParams } from 'src/utils/helpers/queryParam';
import { TypeDTODataPackage } from 'src/utils/typings/dto';
import {
  IGetBuyerAndSupplierByPort,
  IGetListTradeDataEntityPayload,
  IGetListTradeDataHSCodePayload,
  IGetListTradeDataPortPayload,
  IGetTradeDataHSCodeDetail,
  TypeQueryBuyersProportion,
  TypeQueryParamPortTransaction,
} from 'src/utils/typings/dto/tradeData';
import { IDataPackageMapping } from 'src/utils/typings/response';

const TradeDataService = {
  getListTradeDataHSCode,
  getListTradeDataHS6,
  getListPort,
  getListEntity,
  getListTradeDataProduct,
  getListBOLs,
  getBillOfLadingDetail,
  getHSCodeDetail,
  getPortTransactionProportion,
  getListPortOrigin,
  getBuyerAndSupplierByPort,
  getBuyersAndSupplierProportion,
  getBuyersAndSuppliersDetail,
  getSupplierListFromBuyer,
  getListBuyerByPort,
  getListSupplierByPort,
  getTradeDataMarketAnalyticsChart,
  getTradeDataMarketAnalyticsAIExplanation,
  getTradeDataMarketAnalyticsTable,
  getTradeDataMarketAnalyticsWorldMap,
  getBuyerListFromSupplier,
  getSupplierBuyerCountryWorldMap,
  getSupplierBuyerCountryAIExplanation,
  getSupplierBuyerCountryTable,
  getSupplierBuyerCountryCompany,
  getEntityDetail,
  getTransactionsCount,
  getTransactionsPaymentStatus,
  exportListBOLs,
  getListPartnersFromCompany,
  getTradingCompanies,
  getTradeDataMarketAnalyticBuyers,
  getTradeDataMarketAnalyticSuppliers,
  exportDataTableTradeData,
  getListHSCodeAIReport,
  getUnitPriceTableBuyersSuppliers,
  getUnitPriceChartBuyersSuppliers,
  getListDataPackageByType,
  getAverageUnitPriceChart,
  getAverageUnitPriceTable,
};

function getListTradeDataHSCode(query: IGetListTradeDataHSCodePayload) {
  const url = URL_ENDPOINT.tradeData.hsCode;
  return getRequest(url, query, new TradeDataProduct()) as Promise<TradeDataProduct>;
}

function getListTradeDataHS6(query: IGetListTradeDataHSCodePayload) {
  const url = URL_ENDPOINT.tradeData.hs6;
  return getRequest(url, query, new TradeDataProduct()) as Promise<TradeDataProduct>;
}

function getListPort(query: IGetListTradeDataPortPayload) {
  const paramCountry = handleQueryParams('country_code', getArrayDefault(query.country_code));
  const paramRegion = handleQueryParams('region', getArrayDefault(query.region));
  const url = URL_ENDPOINT.tradeData.ports + `?${paramCountry}&${paramRegion}`;
  return getRequest(url, { ...omit(query, ['country_code', 'region']) }, new TradeDataPort()) as Promise<TradeDataPort>;
}

function getListEntity(query: IGetListTradeDataEntityPayload) {
  const url = URL_ENDPOINT.tradeData.entity;
  return getRequest(url, query, new TradeDataEntity()) as Promise<TradeDataEntity>;
}

function getEntityDetail(id: string) {
  const url = URL_ENDPOINT.tradeData.entityDetail.replace(':id', id);
  return getRequest(url, {}, new TradeDataEntityDetail()) as Promise<TradeDataEntityDetail>;
}

function getHSCodeDetail(query: IGetTradeDataHSCodeDetail) {
  const url = URL_ENDPOINT.tradeData.hsCodeDetail.replace(':id', query.code);
  return getRequest(url, {}, new TradeDataProductDetail()) as Promise<TradeDataProductDetail>;
}

function getListTradeDataProduct(query: string) {
  const url = URL_ENDPOINT.company.companies + 'list?' + query;
  return getRequest(url, {}, new TradeDataProduct()) as Promise<TradeDataProduct>;
}

function getListBOLs(query: string) {
  const url = URL_ENDPOINT.tradeData.transactions + '?' + query;
  return getRequest(url, {}, new TradeDataBOLs()) as Promise<TradeDataBOLs>;
}

function getBillOfLadingDetail(id: string) {
  const url = URL_ENDPOINT.tradeData.transactions + '/' + id;
  return getRequest(url, {}, new BillOfLadingDetail()) as Promise<BillOfLadingDetail>;
}
function getPortTransactionProportion(params?: TypeQueryParamPortTransaction) {
  const url = params?.is_exp_port ? URL_ENDPOINT.tradeData.portOriginChart : URL_ENDPOINT.tradeData.portDeliveryChart;
  return getRequest(url, params, new PortOriginTransactionProportions()) as Promise<CommonResponseList>;
}
function getListPortOrigin(params?: TypeQueryParamPortTransaction) {
  const url = params?.is_exp_port ? URL_ENDPOINT.tradeData.portOriginTable : URL_ENDPOINT.tradeData.portDeliverTable;
  return getRequest(url, params, new CommonResponseList()) as Promise<CommonResponseList>;
}
function getBuyerAndSupplierByPort(params: IGetBuyerAndSupplierByPort) {
  const url = URL_ENDPOINT.tradeData.transactions + `/analysis/port/${params.port_code}/list_buyers_suppliers`;
  return getRequest(url, params, new CommonResponseList()) as Promise<CommonResponseList>;
}

function getListBuyerByPort(params: TypeQueryParamPortTransaction, port_id: string) {
  const portOriginURL = replace(URL_ENDPOINT.tradeData.portOriginBuyerList, ':{port_id}', port_id);
  const portDeliveryURL = replace(URL_ENDPOINT.tradeData.portDeliveryBuyerList, ':{port_id}', port_id);
  const url = params.is_exp_port ? portOriginURL : portDeliveryURL;
  return getRequest(url, params, new CommonResponseList()) as Promise<CommonResponseList>;
}
function getListSupplierByPort(params: TypeQueryParamPortTransaction, port_id: string) {
  const portOriginURL = replace(URL_ENDPOINT.tradeData.portOriginSupplierList, ':{port_id}', port_id);
  const portDeliveryURL = replace(URL_ENDPOINT.tradeData.portDeliverySupplierList, ':{port_id}', port_id);
  const url = params.is_exp_port ? portOriginURL : portDeliveryURL;
  return getRequest(url, params, new CommonResponseList()) as Promise<CommonResponseList>;
}
function getBuyersAndSupplierProportion(params?: TypeQueryBuyersProportion) {
  const url = params?.is_buyer ? URL_ENDPOINT.tradeData.buyersChart : URL_ENDPOINT.tradeData.suppliersChart;
  return getRequest(url, params, new CompanyAggregatedByBOL()) as Promise<CompanyAggregatedByBOL>;
}
function getBuyersAndSuppliersDetail(params?: TypeQueryBuyersProportion) {
  const url = params?.is_buyer ? URL_ENDPOINT.tradeData.buyersTable : URL_ENDPOINT.tradeData.suppliersTable;
  return getRequest(url, params, new CompanyAggregatedByBOL()) as Promise<CompanyAggregatedByBOL>;
}
function getSupplierListFromBuyer(params: TypeQueryBuyersProportion, buyer_id: string) {
  const url = URL_ENDPOINT.tradeData.transactions + `/analysis/entity/${buyer_id}/suppliers`;
  return getRequest(url, params, new SupplierFromBuyers()) as Promise<SupplierFromBuyers>;
}
function getBuyerListFromSupplier(params: TypeQueryBuyersProportion, buyer_id: string) {
  const url = URL_ENDPOINT.tradeData.transactions + `/analysis/entity/${buyer_id}/buyers`;
  return getRequest(url, params, new BuyerFromSuppliers()) as Promise<BuyerFromSuppliers>;
}

function getListPartnersFromCompany(
  params: TypeQueryBuyersProportion,
  company_id: string,
  partnerType: TypeBuyerSupplier,
) {
  const suppliersListBuyersURL = replace(URL_ENDPOINT.tradeData.suppliersListBuyers, ':{supplier_id}', company_id);
  const buyersListSuppliersURL = replace(URL_ENDPOINT.tradeData.buyersListSuppliers, ':{buyer_id}', company_id);
  const url = partnerType === TypeBuyerSupplier.buyer ? suppliersListBuyersURL : buyersListSuppliersURL;
  return getRequest(url, params, new PartnersFromCompany()) as Promise<PartnersFromCompany>;
}

function getTradeDataMarketAnalyticsChart(query: any) {
  const url = URL_ENDPOINT.tradeData.marketAnalyticsChart;
  return getRequest(url, query, new TradeDataMarketAnalytics()) as Promise<TradeDataMarketAnalytics>;
}

function getTradeDataMarketAnalyticsAIExplanation(query: any) {
  const url = URL_ENDPOINT.tradeData.marketAnalyticsAIExplanation;
  return getRequest(
    url,
    query,
    new TradeDataMarketAnalyticsAIExplanation(),
  ) as Promise<TradeDataMarketAnalyticsAIExplanation>;
}

function getTradeDataMarketAnalyticsTable(query: any) {
  const url = URL_ENDPOINT.tradeData.marketAnalyticsTable;
  return getRequest(url, query, new TradeDataMarketAnalytics()) as Promise<TradeDataMarketAnalytics>;
}

function getTradeDataMarketAnalyticBuyers(query: any) {
  const url = URL_ENDPOINT.tradeData.marketAnalyticBuyers;
  return getRequest(url, query, new TradeDataMarketAnalyticsCompany()) as Promise<TradeDataMarketAnalyticsCompany>;
}

function getTradeDataMarketAnalyticSuppliers(query: any) {
  const url = URL_ENDPOINT.tradeData.marketAnalyticSuppliers;
  return getRequest(url, query, new TradeDataMarketAnalyticsCompany()) as Promise<TradeDataMarketAnalyticsCompany>;
}

function getTradeDataMarketAnalyticsWorldMap(query: any) {
  const url = URL_ENDPOINT.tradeData.marketAnalyticsWorldMap;
  return getRequest(url, query, new TradeDataMarketAnalyticsWorldMap()) as Promise<TradeDataMarketAnalyticsWorldMap>;
}

function getSupplierBuyerCountryWorldMap(query?: any) {
  const url = query?.is_buyer
    ? URL_ENDPOINT.tradeData.buyerCountryWorldMap
    : URL_ENDPOINT.tradeData.supplierCountryWorldMap;
  return getRequest(url, query, new TradeDataListTypeResponse()) as Promise<TradeDataListTypeResponse>;
}

function getSupplierBuyerCountryAIExplanation(query?: any) {
  const url = query?.is_buyer
    ? URL_ENDPOINT.tradeData.buyerCountryAIExplanation
    : URL_ENDPOINT.tradeData.supplierCountryAIExplanation;
  return getRequest(url, query, new TradeDataListTypeAIExplanation()) as Promise<TradeDataListTypeAIExplanation>;
}

function getSupplierBuyerCountryTable(query: any) {
  const url = query?.is_buyer ? URL_ENDPOINT.tradeData.buyerCountryTable : URL_ENDPOINT.tradeData.supplierCountryTable;
  return getRequest(url, query, new TradeDataListTypeResponse()) as Promise<TradeDataListTypeResponse>;
}

function getSupplierBuyerCountryCompany(query: any) {
  const url = query?.is_buyer
    ? URL_ENDPOINT.tradeData.buyerCountryCompany
    : URL_ENDPOINT.tradeData.supplierCountryCompany;
  return getRequest(url, query, new TradeDataMarketAnalyticsCompany()) as Promise<TradeDataMarketAnalyticsCompany>;
}

function getTransactionsCount(query: any) {
  const url = URL_ENDPOINT.tradeData.transactionsCount;
  return getRequest(url, query, { data: 0, status_code: 200 }) as Promise<{ data: number }>;
}

function getTransactionsPaymentStatus(query: any) {
  const url = URL_ENDPOINT.tradeData.transactionsPaymentStatus;
  return getRequest(url, query) as Promise<{ data: ITransactionPaymentStatus }>;
}

function getTradingCompanies(query: any) {
  const url = URL_ENDPOINT.tradeData.companiesByKeyword;
  return getRequest(url, query, { data: [], status_code: 200 }) as Promise<{ data: ITradingCompany[] }>;
}

function exportListBOLs(query: string) {
  const url = URL_ENDPOINT.tradeData.transactions + '/export?' + query;
  return getBlobRequest(
    url,
    {},
    {
      accept: 'application/json',
    },
  );
}

function exportDataTableTradeData(url: string, params: Record<string, any>) {
  return getBlobRequest(url, params);
}

function getListHSCodeAIReport(query: IGetListTradeDataHSCodePayload) {
  const url = URL_ENDPOINT.tradeData.reportHSCodeSearch;
  return getRequest(url, query, new TradeDataProduct()) as Promise<TradeDataProduct>;
}

function getUnitPriceTableBuyersSuppliers(params?: TypeQueryParamPortTransaction) {
  const url = params?.is_buyer
    ? URL_ENDPOINT.tradeData.unitPriceBuyersTable
    : URL_ENDPOINT.tradeData.unitPriceSuppliersTable;
  return getRequest(url, params, new CommonResponseList()) as Promise<CommonResponseList>;
}

function getUnitPriceChartBuyersSuppliers(params?: TypeQueryParamPortTransaction) {
  const url = params?.is_buyer
    ? URL_ENDPOINT.tradeData.unitPriceBuyersChart
    : URL_ENDPOINT.tradeData.unitPriceSuppliersChart;
  return getRequest(url, params, new CommonResponse()) as Promise<CommonResponse>;
}

function getAverageUnitPriceChart(params?: TypeQueryParamPortTransaction) {
  const url = URL_ENDPOINT.tradeData.averageUnitPriceChart;
  return getRequest(url, params, new CommonResponse()) as Promise<CommonResponse>;
}

function getListDataPackageByType<T extends TypeDataPackage>(type: T, params: TypeDTODataPackage) {
  const url = URL_ENDPOINT.tradeData.data_package[type];
  return getRequest(url, params, new CommonResponse()) as Promise<IResponse<IDataPackageMapping[T][]>>;
}

function getAverageUnitPriceTable(params?: TypeQueryParamPortTransaction) {
  const url = URL_ENDPOINT.tradeData.averageUnitPriceTable;
  return getRequest(url, params, new CommonResponseList()) as Promise<CommonResponseList>;
}

export default TradeDataService;
